import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { useWindowSize } from 'react-use';

import { CodeHighlightContext } from '../../context/CodeHighlightContext';
import { InteractiveCodeContext } from '../../context/InteractiveCodeContext';
import { PageContext } from '../../context/PageContext';
import { useInteractiveCodeScrolling } from '../../hooks/useInteractiveCodeScrolling';
import { useInteractiveLanguage } from '../../hooks/useInteractiveLanguage';
import { Interactive } from '../CodeBlock/Interactive';

import * as classes from './style.module.scss';

interface Props {
  files: string[];
}

export const InteractiveLanguage: React.FC<Props> = ({
  files: codeBlockFiles,
  children,
}) => {
  const { pageTitle } = useContext(PageContext);

  const uuid = useRef(uniqueId('interactive-language'));

  const [columnLayout, setColumnLayout] = useState<boolean>(false);
  const [activeLines, setLines] = useState<Array<string | number>>([]);
  const [activeElement, setActiveElement] = useState<string | null>(null);
  const [userSelectedBlock, setUserSelectedBlock] = useState<string | null>(
    null,
  );
  const [codeBlockIndex, setCodeBlockIndex] = useState<number>(0);
  const [codeBlocks, processedChildren, codeMap, files] =
    useInteractiveLanguage(
      children,
      uuid.current,
      columnLayout,
      codeBlockFiles,
    );

  const { width } = useWindowSize();

  useEffect(() => {
    setColumnLayout(width < 767);
  }, [width]);

  // reset active lines if the user clicked on different code block
  const changeCodeBlock = (value: number): void => {
    setCodeBlockIndex(value);
    setLines([]);
    setActiveElement(null);
  };

  const nonUserSetActiveLines = (id: string | null) => {
    if (id !== activeElement) {
      setLines(id ? codeMap[id].highlight : []);
      // keep the last file in view until a new one is selected
      setCodeBlockIndex(id ? codeMap[id].codeBlockIndex : codeBlockIndex);
      setActiveElement(id);
    }
  };

  const setActiveLines = (id: string | null) => {
    if (id !== activeElement) {
      nonUserSetActiveLines(id);
      setUserSelectedBlock(id);
    }
  };

  useInteractiveCodeScrolling(
    uuid.current,
    userSelectedBlock,
    columnLayout,
    nonUserSetActiveLines,
    setUserSelectedBlock,
  );

  const interactiveCodeContextValue = useMemo(
    () => ({ codeBlockIndex, files, setCodeBlockIndex: changeCodeBlock }),
    [codeBlockIndex, files, changeCodeBlock],
  );

  const codeHighlightContextValue = useMemo(
    () => ({ activeElement, activeLines, setActiveLines }),
    [activeElement, activeLines, setActiveLines],
  );

  return (
    <InteractiveCodeContext.Provider value={interactiveCodeContextValue}>
      <CodeHighlightContext.Provider value={codeHighlightContextValue}>
        <div
          className={classNames(classes.root, {
            [classes.columnRoot]: columnLayout,
          })}
        >
          <div
            data-testid="content"
            className={classNames(classes.text, {
              [classes.columnText]: columnLayout,
            })}
          >
            {pageTitle && <h1>{pageTitle}</h1>}
            {processedChildren}
          </div>
          <div
            className={classNames(classes.code, {
              [classes.columnCode]: columnLayout,
            })}
          >
            {!columnLayout && <Interactive codeBlocks={codeBlocks} />}
          </div>
        </div>
      </CodeHighlightContext.Provider>
    </InteractiveCodeContext.Provider>
  );
};
